
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showCredentials, setShowCredentials] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const success = await login(email, password);
      if (success) {
        navigate('/');
      } else {
        setError('Invalid email or password. Please try again.');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const quickLogin = (userEmail: string, userPassword: string) => {
    setEmail(userEmail);
    setPassword(userPassword);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-red-50 to-gray-100 flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full">
        {/* Logo and Brand Section */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-[#C41230] to-red-700 rounded-2xl shadow-lg mb-4 transform hover:scale-105 transition-transform">
            <span className="text-4xl">🎓</span>
          </div>
          <h1 className="text-4xl font-bold text-gray-800 mb-2">
            Campus Link
          </h1>
          <p className="text-lg text-[#C41230] font-semibold tracking-wide">
            Tartan World
          </p>
          <p className="text-gray-600 mt-2">
            Your gateway to personalized learning
          </p>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">
            Welcome Back
          </h2>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C41230] focus:border-transparent outline-none transition"
                placeholder="you@cmu.edu"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C41230] focus:border-transparent outline-none transition"
                placeholder="Enter your password"
              />
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-[#C41230] to-red-700 text-white py-3 px-4 rounded-lg font-semibold hover:from-red-700 hover:to-[#C41230] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#C41230] disabled:opacity-50 disabled:cursor-not-allowed transition-all transform hover:scale-[1.02] active:scale-[0.98]"
            >
              {isLoading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Signing In...
                </span>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          {/* Demo Credentials Section */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <button
              onClick={() => setShowCredentials(!showCredentials)}
              className="text-sm text-gray-600 hover:text-[#C41230] flex items-center justify-center w-full transition"
            >
              <span className="mr-2">🔑</span>
              {showCredentials ? 'Hide' : 'Show'} Demo Credentials
            </button>

            {showCredentials && (
              <div className="mt-4 space-y-3">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-xs font-semibold text-blue-900 mb-2">Student Account:</p>
                  <div className="flex justify-between items-center text-xs text-blue-700">
                    <span>student@cmu.edu / student123</span>
                    <button
                      onClick={() => quickLogin('student@cmu.edu', 'student123')}
                      className="text-blue-600 hover:text-blue-800 font-medium"
                    >
                      Use
                    </button>
                  </div>
                </div>
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                  <p className="text-xs font-semibold text-purple-900 mb-2">Admin Account:</p>
                  <div className="flex justify-between items-center text-xs text-purple-700">
                    <span>admin@cmu.edu / admin123</span>
                    <button
                      onClick={() => quickLogin('admin@cmu.edu', 'admin123')}
                      className="text-purple-600 hover:text-purple-800 font-medium"
                    >
                      Use
                    </button>
                  </div>
                </div>
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-xs font-semibold text-green-900 mb-2">Demo Account:</p>
                  <div className="flex justify-between items-center text-xs text-green-700">
                    <span>demo@cmu.edu / demo123</span>
                    <button
                      onClick={() => quickLogin('demo@cmu.edu', 'demo123')}
                      className="text-green-600 hover:text-green-800 font-medium"
                    >
                      Use
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-gray-600 mt-6">
          Need help? Contact{' '}
           <a href="mailto:mkoome@andrew.cmu.edu" className="text-[#C41230] hover:underline">
            mkoome@andrew.cmu.edu
          </a>{' '}
          or{' '}
          <a href="mailto:ianyaele@andrew.cmu.edu" className="text-[#C41230] hover:underline">
            ianyaele@andrew.cmu.edu
          </a>
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
